import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight16Regular, DocumentBulletList20Regular } from '@fluentui/react-icons';

import { IAssociatedPriceList } from '../../../models/Entities/PriceList/IAssociatedPriceList';
import { onFormatDate, parseUTCDateAsLocal } from '../../../commons/functions/dateUtils';

// guía
import { StatusPill } from '../../../theme/components/StatusPill';
import { GuideSkeleton } from '../../../theme/components/GuideSkeleton';
import { GuideEmptyState } from '../../../theme/components/GuideEmptyState';

// lógica
import { priceListStatus } from '../PriceList.Logic';

import './PriceListCards.Styles.css';

export interface PriceListCardsProps<T extends IAssociatedPriceList> {
  priceList: T[];
  onSelectPriceList?: (priceList: T) => void;
  /** Selección controlada desde la página (p. ej. para limpiarla al borrar la lista). */
  selectedId?: number;
  loading?: boolean;
  loadingLabel: string;
  emptyTitle: string;
  emptyText?: string;
  /** Acción del estado vacío (normalmente el botón de agregar lista). */
  emptyAction?: React.ReactNode;
  testId?: string;
}

const validityLabel = (list: IAssociatedPriceList, t: (key: string, options?: Record<string, unknown>) => string): string => {
  const from = list.effectiveDateStart ? onFormatDate(parseUTCDateAsLocal(list.effectiveDateStart)) : undefined;
  const to = list.effectiveDateEnd ? onFormatDate(parseUTCDateAsLocal(list.effectiveDateEnd)) : undefined;
  if (from && to) return t('priceListCards.validity.range', { from, to });
  if (from) return t('priceListCards.validity.from', { from });
  if (to) return t('priceListCards.validity.until', { to });
  return t('priceListCards.validity.none');
};

/**
 * Selector de listas de precios como tarjetas de la guía (proveedor, convenios): nombre en bold,
 * vigencia en secundario y etiqueta de estado; la elegida con fondo primario tenue y barra azul.
 */
export const PriceListCards = <T extends IAssociatedPriceList>(props: PriceListCardsProps<T>) => {
  const [t] = useTranslation();
  const [internalSelectedId, setInternalSelectedId] = useState<number | undefined>(undefined);
  const selectedId = props.selectedId !== undefined ? props.selectedId : internalSelectedId;

  const onSelect = (list: T) => {
    setInternalSelectedId(list.id);
    props.onSelectPriceList?.(list);
  };

  // Si la lista elegida cambió en el store (nombre, vigencia), se avisa con la versión nueva.
  useEffect(() => {
    if (selectedId === undefined) return;
    const updated = props.priceList.find((item) => item.id === selectedId);
    if (updated) props.onSelectPriceList?.(updated);
  }, [props.priceList]); // eslint-disable-line react-hooks/exhaustive-deps

  if (props.loading && (!props.priceList || props.priceList.length === 0)) {
    return <GuideSkeleton rows={3} label={props.loadingLabel} data-testid={`${props.testId ?? 'plc'}-loading`} />;
  }

  if (!props.priceList || props.priceList.length === 0) {
    return <GuideEmptyState title={props.emptyTitle} text={props.emptyText} action={props.emptyAction} data-testid={`${props.testId ?? 'plc'}-empty`} />;
  }

  return (
    <ul className="plc-list" role="listbox" aria-label={t('priceListCards.ariaLabel')} data-testid={props.testId ?? 'plc-list'}>
      {props.priceList.map((item) => {
        const isSelected = item.id === selectedId;
        const status = priceListStatus(item);
        return (
          <li key={item.id} role="option" aria-selected={isSelected}>
            <button type="button" className={`plc-card${isSelected ? ' is-selected' : ''}`} onClick={() => onSelect(item)} data-testid={`plc-card-${item.id}`}>
              <span className="plc-card__icon" aria-hidden="true"><DocumentBulletList20Regular /></span>
              <span className="plc-card__body">
                <span className="plc-card__title" title={item.priceList?.listName}>{item.priceList?.listName}</span>
                <span className="plc-card__validity">{validityLabel(item, t as any)}</span>
                <StatusPill tone={status.tone} symbol={status.symbol} size="small">{t(status.i18nKey)}</StatusPill>
              </span>
              <span className="plc-card__chevron" aria-hidden="true"><ChevronRight16Regular /></span>
            </button>
          </li>
        );
      })}
    </ul>
  );
};

export default PriceListCards;
